import type { Group, Mesh, Object3D } from 'three';

import type { KitchenSceneBuilder } from '../kitchen-scene-builder.class';
import type { AddPanelInfo } from '../../../../model/add-panel-info';
import { TextureType } from '../../../../model/enums';
import { normalizeSurroundKey, normalizeCountertopKey, computeGeometryHash } from '../helpers';

/**
 * Create kitchen geometry (panels, trims, countertops)
 */
export const createKitchenGeometryCfn = async (
	self: KitchenSceneBuilder,
	_params: Record<string, never>
): Promise<void> => {
	const islands = self.kitchenDataManagerAccess.getIslands();
	const panelPromises: Promise<void>[] = [];

	for (let islandIndex = 0; islandIndex < islands.length; islandIndex++) {
		const island = islands[islandIndex];
		const islandGroup = self.islandGroupsAccess[islandIndex];
		if (!islandGroup) {
			console.warn('KitchenSceneBuilder.createKitchenGeometry: No island group for index', islandIndex);
			continue;
		}

		const surroundKey = normalizeSurroundKey(self.kitchenDataManagerAccess.getKitchenMaterial()?.surround?.key);

		// Add panels (surround)
		if (island.panels) {
			for (let panelIndex = 0; panelIndex < island.panels.length; panelIndex++) {
				const panel = island.panels[panelIndex];
				panelPromises.push(createSurroundPanelCfn(self, { panel, islandIndex, panelIndex, islandGroup, surroundKey }));
			}
		}

		// Add panel trims (corners)
		if (island.panelTrims) {
			for (let trimIndex = 0; trimIndex < island.panelTrims.length; trimIndex++) {
				const trim = island.panelTrims[trimIndex];
				panelPromises.push(createTrimPanelCfn(self, { trim, islandIndex, trimIndex, islandGroup, surroundKey }));
			}
		}

		// Add countertop -- resolve the key from the per-island countertop material
		// (honors a per-island "No Countertop"/material override), not the kitchen default.
		if (island.counter) {
			const countertopKey = normalizeCountertopKey(island.counter?.material?.key);
			panelPromises.push(createCountertopCfn(self, { island, islandIndex, islandGroup, countertopKey }));
		}
	}

	await Promise.all(panelPromises);
};

/**
 * Create surround panel
 */
export const createSurroundPanelCfn = async (
	self: KitchenSceneBuilder,
	{ panel, islandIndex, panelIndex, islandGroup, surroundKey }: {
		panel: any;
		islandIndex: number;
		panelIndex: number;
		islandGroup: Group;
		surroundKey?: string;
	}
): Promise<void> => {
	const panelCorners = panel.corners || {};
	const corners = panelCorners.corners || [];
	if (!corners || corners.length === 0) return;

	// Convert corners to vertices (flip Y for Three.js)
	const vertices: Array<{ x: number; y: number }> = [];
	for (const corner of corners) {
		vertices.push({
			x: corner.x || 0,
			y: -(corner.y || 0)
		});
	}

	if (vertices.length < 3) return;

	// Get panel center
	const panelCenter = panel.translatedCenter || panel.center || { x: 0, y: 0, z: 0 };
	const surroundCenter = {
		x: panelCenter.x || 0,
		y: -(panelCenter.y || 0),
		z: panelCenter.z || 0
	};

	// originAngle is stored in radians, convert back to degrees for GeometryBuilder
	const originAngleDegrees = ((panel.originAngle || 0) * 180) / Math.PI;

	// Use stable ID: prefer panel.id from data, fallback to index-based ID
	const panelId = panel.id || `panel-${islandIndex}-${panelIndex}`;

	const panelRotation = { x: 0, y: originAngleDegrees, z: 0 };
	const panelDepth = (panel.size?.depth ?? panel.thickness ?? 1);

	const addPanelInfo: AddPanelInfo = {
		islandIndex,
		id: panelId,
		vertices,
		size: panel.size || { depth: panel.thickness || 1 },
		rotation: panelRotation,
		center: surroundCenter,
		textureKey: surroundKey,
		panelType: TextureType.Surround,
		modelPath: self.kitchenDataManagerAccess.getModelPath(surroundKey || '') || undefined,
		hash: computeGeometryHash(vertices, panelDepth, panelRotation)
	};

	try {
		const result = await self.geometryBuilderAccess.createPanel(addPanelInfo);
		result.mesh.userData.islandId = islandGroup.userData?.id;
		result.mesh.userData.islandCenter = islandGroup.userData?.center;
		islandGroup.add(result.mesh);
	} catch (error) {
		console.error('KitchenSceneBuilder.createSurroundPanel: Error creating panel:', error);
	}
};

/**
 * Create trim panel (corner)
 */
export const createTrimPanelCfn = async (
	self: KitchenSceneBuilder,
	{ trim, islandIndex, trimIndex, islandGroup, surroundKey }: {
		trim: any;
		islandIndex: number;
		trimIndex: number;
		islandGroup: Group;
		surroundKey?: string;
	}
): Promise<void> => {
	// MOK surrounds have no visible corner trims
	if (surroundKey?.startsWith('moks/')) return;

	const rd = trim.renderingDetails;
	if (!rd || !rd.vertices || rd.vertices.length === 0) return;

	const vertices: Array<{ x: number; y: number }> = [];
	for (const v of rd.vertices) {
		vertices.push({
			x: v.x || 0,
			y: -(v.y || 0)
		});
	}

	if (vertices.length < 3) return;

	const trimCenter = trim.center || { x: 0, y: 0, z: 0 };

	// Use stable ID: prefer trim.id from data, fallback to index-based ID
	const trimId = trim.id || `trim-${islandIndex}-${trimIndex}`;

	const trimRotation = { x: -90, y: 0, z: 0 };
	const trimDepth = rd.depth || 1;
	const trimTextureKey = `${surroundKey}-corner`;

	const addPanelInfo: AddPanelInfo = {
		islandIndex,
		id: trimId,
		vertices,
		size: { depth: trimDepth },
		rotation: trimRotation,
		center: {
			x: trimCenter.x || 0,
			y: trimCenter.y || 0,
			z: trimCenter.z || 0
		},
		textureKey: trimTextureKey,
		panelType: TextureType.Trim,
		hash: computeGeometryHash(vertices, trimDepth, trimRotation)
	};

	try {
		const result = await self.geometryBuilderAccess.createPanel(addPanelInfo);
		result.mesh.userData.islandId = islandGroup.userData?.id;
		islandGroup.add(result.mesh);
	} catch (error) {
		console.error('KitchenSceneBuilder: Error creating trim panel:', error);
	}
};

/**
 * Create countertop
 */
export const createCountertopCfn = async (
	self: KitchenSceneBuilder,
	{ island, islandIndex, islandGroup, countertopKey }: {
		island: any;
		islandIndex: number;
		islandGroup: Group;
		countertopKey?: string;
	}
): Promise<void> => {
	const { counter } = island;
	const rd = counter.renderingDetails;
	if (!rd || !rd.vertices || rd.vertices.length === 0) return;

	const vertices: Array<{ x: number; y: number }> = [];
	for (const v of rd.vertices) {
		vertices.push({
			x: v.x || 0,
			y: v.y || 0 // Countertop doesn't flip Y
		});
	}

	if (vertices.length < 3) return;

	const islandSize = island.size || { height: 0 };
	const counterCenter = {
		x: 0,
		y: (islandSize.height || 0) + (rd.depth || 0) / 2,
		z: 0
	};

	const counterRotation = { x: 90, y: 0, z: 0 };
	const counterDepth = rd.depth || 1;

	const addPanelInfo: AddPanelInfo = {
		islandIndex,
		// Use a stable ID (even when cutouts are disabled) so incremental diffing is consistent
		id: `island-${islandIndex}-countertop`,
		vertices,
		size: { depth: counterDepth },
		rotation: counterRotation,
		center: counterCenter,
		textureKey: countertopKey,
		panelType: TextureType.Counter,
		hash: computeGeometryHash(vertices, counterDepth, counterRotation)
	};

	try {
		const result = await self.geometryBuilderAccess.createPanel(addPanelInfo);
		result.mesh.userData.islandId = islandGroup.userData?.id;
		// The mesh id stays synthetic so scene diffing keeps working; the domain id rides alongside
		// it so a click can resolve the countertop in the blueprint.
		result.mesh.userData.countertopId = counter.id ?? null;
		islandGroup.add(result.mesh);
	} catch (error) {
		console.error('KitchenSceneBuilder: Error creating countertop:', error);
	}
};

// ========== Object Creation Methods (return mesh without adding to group) ==========

/**
 * Create surround panel and return it (for progressive building)
 */
export const createSurroundPanelObjectCfn = async (
	self: KitchenSceneBuilder,
	{ panel, islandIndex, panelIndex, surroundKey }: {
		panel: any;
		islandIndex: number;
		panelIndex: number;
		surroundKey?: string;
	}
): Promise<{ mesh: Object3D; id: string } | null> => {
	const panelCorners = panel.corners || {};
	const corners = panelCorners.corners || [];
	if (!corners || corners.length === 0) return null;

	const vertices: Array<{ x: number; y: number }> = [];
	for (const corner of corners) {
		vertices.push({
			x: corner.x || 0,
			y: -(corner.y || 0)
		});
	}

	if (vertices.length < 3) return null;

	const panelCenter = panel.translatedCenter || panel.center || { x: 0, y: 0, z: 0 };
	const surroundCenter = {
		x: panelCenter.x || 0,
		y: -(panelCenter.y || 0),
		z: panelCenter.z || 0
	};

	const originAngleDegrees = ((panel.originAngle || 0) * 180) / Math.PI;
	const panelId = panel.id || `panel-${islandIndex}-${panelIndex}`;

	const objPanelRotation = { x: 0, y: originAngleDegrees, z: 0 };
	const objPanelDepth = (panel.size?.depth ?? panel.thickness ?? 1);

	const addPanelInfo: AddPanelInfo = {
		islandIndex,
		id: panelId,
		vertices,
		size: panel.size || { depth: panel.thickness || 1 },
		rotation: objPanelRotation,
		center: surroundCenter,
		textureKey: surroundKey,
		panelType: TextureType.Surround,
		modelPath: self.kitchenDataManagerAccess.getModelPath(surroundKey || '') || undefined,
		hash: computeGeometryHash(vertices, objPanelDepth, objPanelRotation)
	};

	try {
		const result = await self.geometryBuilderAccess.createPanel(addPanelInfo);
		return { mesh: result.mesh, id: panelId };
	} catch (error) {
		console.error('KitchenSceneBuilder.createSurroundPanelObject: Error creating panel:', error);
		return null;
	}
};

/**
 * Create trim panel and return it (for progressive building)
 */
export const createTrimPanelObjectCfn = async (
	self: KitchenSceneBuilder,
	{ trim, islandIndex, trimIndex, surroundKey }: {
		trim: any;
		islandIndex: number;
		trimIndex: number;
		surroundKey?: string;
	}
): Promise<{ mesh: Object3D; id: string } | null> => {
	// MOK surrounds have no visible corner trims
	if (surroundKey?.startsWith('moks/')) return null;

	const rd = trim.renderingDetails;
	if (!rd || !rd.vertices || rd.vertices.length === 0) return null;

	const vertices: Array<{ x: number; y: number }> = [];
	for (const v of rd.vertices) {
		vertices.push({
			x: v.x || 0,
			y: -(v.y || 0)
		});
	}

	if (vertices.length < 3) return null;

	const trimCenter = trim.center || { x: 0, y: 0, z: 0 };
	const trimId = trim.id || `trim-${islandIndex}-${trimIndex}`;

	const objTrimRotation = { x: -90, y: 0, z: 0 };
	const objTrimDepth = rd.depth || 1;
	const objTrimTextureKey = `${surroundKey}-corner`;

	const addPanelInfo: AddPanelInfo = {
		islandIndex,
		id: trimId,
		vertices,
		size: { depth: objTrimDepth },
		rotation: objTrimRotation,
		center: {
			x: trimCenter.x || 0,
			y: trimCenter.y || 0,
			z: trimCenter.z || 0
		},
		textureKey: objTrimTextureKey,
		panelType: TextureType.Trim,
		hash: computeGeometryHash(vertices, objTrimDepth, objTrimRotation)
	};

	try {
		const result = await self.geometryBuilderAccess.createPanel(addPanelInfo);
		return { mesh: result.mesh, id: trimId };
	} catch (error) {
		console.error('KitchenSceneBuilder: Error creating trim panel:', error);
		return null;
	}
};

/**
 * Create countertop and return it (for progressive building)
 */
export const createCountertopObjectCfn = async (
	self: KitchenSceneBuilder,
	{ island, islandIndex, countertopKey }: {
		island: any;
		islandIndex: number;
		countertopKey?: string;
	}
): Promise<{ mesh: Object3D; id: string } | null> => {
	const { counter } = island;
	if (!counter) return null;

	const rd = counter.renderingDetails;
	if (!rd || !rd.vertices || rd.vertices.length === 0) return null;

	const vertices: Array<{ x: number; y: number }> = [];
	for (const v of rd.vertices) {
		vertices.push({
			x: v.x || 0,
			y: v.y || 0
		});
	}

	if (vertices.length < 3) return null;

	const islandSize = island.size || { height: 0 };
	const counterCenter = {
		x: 0,
		y: (islandSize.height || 0) + (rd.depth || 0) / 2,
		z: 0
	};

	const countertopId = `island-${islandIndex}-countertop`;

	const objCounterRotation = { x: 90, y: 0, z: 0 };
	const objCounterDepth = rd.depth || 1;

	const addPanelInfo: AddPanelInfo = {
		islandIndex,
		id: countertopId,
		vertices,
		size: { depth: objCounterDepth },
		rotation: objCounterRotation,
		center: counterCenter,
		textureKey: countertopKey,
		panelType: TextureType.Counter,
		hash: computeGeometryHash(vertices, objCounterDepth, objCounterRotation)
	};

	try {
		const result = await self.geometryBuilderAccess.createPanel(addPanelInfo);
		return { mesh: result.mesh, id: countertopId };
	} catch (error) {
		console.error('KitchenSceneBuilder: Error creating countertop:', error);
		return null;
	}
};

// ========== Targeted Surround Rebuild ==========

/**
 * Rebuild only surround panels and corner trims within existing island groups.
 * Leaves countertops, appliances, and all other scene objects intact.
 * Used for slat <-> image texture transitions to avoid a full scene rebuild.
 */
export const rebuildSurroundPanelsCfn = async (
	self: KitchenSceneBuilder,
	{ islandGroups, surroundKey }: { islandGroups: Group[]; surroundKey: string }
): Promise<void> => {
	const islands = self.kitchenDataManagerAccess.getIslands();
	const panelPromises: Promise<void>[] = [];

	for (let islandIndex = 0; islandIndex < islandGroups.length && islandIndex < islands.length; islandIndex++) {
		const islandGroup = islandGroups[islandIndex];
		const island = islands[islandIndex];

		// Remove existing panel and corner meshes (leave countertops and appliances intact)
		const childrenCopy = [...islandGroup.children];
		for (const child of childrenCopy) {
			const mesh = child as Mesh & { userData?: { type?: string }; geometry?: { dispose: () => void }; material?: unknown };
			const type = mesh.userData?.type;
			if (type === 'panel' || type === 'corner') {
				if (mesh.geometry) {
					mesh.geometry.dispose();
				}
				if (mesh.material) {
					const mat = mesh.material;
					if (Array.isArray(mat)) {
						mat.forEach((m: { dispose?: () => void }) => m.dispose?.());
					} else {
						(mat as { dispose?: () => void }).dispose?.();
					}
				}
				islandGroup.remove(child);
			}
		}

		// Create new surround panels
		if (island.panels) {
			for (let panelIndex = 0; panelIndex < island.panels.length; panelIndex++) {
				const panel = island.panels[panelIndex];
				panelPromises.push(createSurroundPanelCfn(self, { panel, islandIndex, panelIndex, islandGroup, surroundKey }));
			}
		}

		// Create new trim panels
		if (island.panelTrims) {
			for (let trimIndex = 0; trimIndex < island.panelTrims.length; trimIndex++) {
				const trim = island.panelTrims[trimIndex];
				panelPromises.push(createTrimPanelCfn(self, { trim, islandIndex, trimIndex, islandGroup, surroundKey }));
			}
		}
	}

	await Promise.all(panelPromises);
};
